import React, { useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import HubPageLayout from '../components/HubPageLayout';
import FramedPanel from '../components/FramedPanel';
import SplitSection from '../components/SplitSection';
import { ASSESSMENTS, assessmentPath } from './assessmentConfig';
import { RATING_BANDS, getRating } from './assessmentScoring';

// Shared results page for both MoSAIC PSYOPS behavioral screenings.
// The score arrives in router state from the assessment page; it is never
// persisted, so a direct visit or refresh prompts the user to take the
// screening rather than showing a stale result.

const SECTIONS = [
  {
    heading: 'Your Reaction',
    paragraphs: [
      'Perhaps your score was high — ginning up feelings of anger, disbelief — no way, I found my soulmate. This is a can’t-miss investment. My score — it’s wrong.',
      'On the other hand, perhaps your score was low and you have a sense of relief that everything is fine — no scam here.',
      'Understand that scammers use an abuse playbook built on deception, lies and half-truths designed to exploit you and steal your money. The scam starts off small to lay the foundation for more complex schemes, with the goal of decimating their target’s bank account.',
      'Coming to grips with being the target of a scammer is surreal. In the online world you always have to be on guard, and these digital predators understand the use of social engineering (dark psychology) techniques to lure you in and exploit your hopes and dreams — all to steal your money. You are a business transaction.',
    ],
  },
  {
    heading: 'Abuser Reaction — You can’t believe that',
    paragraphs: [
      'Confronting your abuser takes tremendous courage. It is a deeply personal decision, driven by the desire to understand why. This will be a direct assault on an abuser’s influence, power, control and revenue pipeline. Rest assured, they will not "go gently into that good night." They will fight with everything they have and use every predatory technique they know to keep you from defecting.',
      'Depending on the scheme they could play the sympathy card one second and then turn around and use negative emotional blackmail the next. Don’t be surprised at threats of violence designed to throw you off balance — anything to keep you under their control.',
      'It is a given they will call the assessment fake — "not true," "don’t believe that, it’s a lie," "you’re missing out on the opportunity of a lifetime," "I love you." They will say and do anything to keep you on the hook.',
      'Grooming is a covert, deceptive and cruel practice designed to hurt and abuse the target. Depending on your score you might have feelings that range from disbelief to anger or relief. No matter the emotion, they are completely normal; however, emotions don’t change reality — facts are not subject to feelings, nor to their impact.',
    ],
  },
  {
    heading: 'Breaking Free',
    paragraphs: [
      'Taking back your independence is not for the faint of heart. It takes courage, and it will require you to prepare for the emotional and mental battle that lies ahead.',
      'You are the only one who can reclaim your independence. You will need to lean on your support system — family and friends, counselors — but in the end, this will be up to you.',
    ],
  },
];

const TRUSTWORTHY = 'All No / SCAM MoSAIC PSYOPS behavioral screenings are structured, evidence-based evaluations that focus on understanding an individual’s interaction patterns. The primary goal is to give you a personal, private tool that helps you identify behaviors, understand context, and analyze the response against the scammer abuse playbook — providing a precautionary warning based upon known scammer predatory tripwires.';

const BandTable = ({ cells }) => (
  <div className="border border-gray-300 p-4">
    <div className="grid grid-cols-4">
      {RATING_BANDS.map((band) => (
        <div key={band.id} className={`${band.color} text-white text-center py-2`}>
          {band.label}
        </div>
      ))}
      {RATING_BANDS.map((band) => (
        <div key={band.id} className={`${band.color} text-white text-center py-2 border-t border-white/30`}>
          {cells[band.id]}
        </div>
      ))}
    </div>
  </div>
);

// `slug` is passed by the route; useParams is the fallback if this is ever
// mounted under a parameterised path.
const AssessmentResultsPage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const location = useLocation();
  const [panel, setPanel] = useState('vulnerabilities');

  const assessment = ASSESSMENTS[slug];
  const result = location.state && location.state.slug === slug ? location.state : null;

  if (!assessment) {
    return (
      <HubPageLayout>
        <p className="py-12 text-center text-gray-500">Unknown assessment.</p>
      </HubPageLayout>
    );
  }

  // No score in hand — screenings are never recorded, so offer to take one.
  if (!result) {
    return (
      <HubPageLayout>
        <FramedPanel title={assessment.title}>
          <div className="px-6 pt-10 pb-10 text-center">
            <p className="text-black text-lg">
              Results are private and are never recorded, so there is nothing to show here yet.
            </p>
            <Link
              to={assessmentPath(slug)}
              className="inline-block mt-6 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-900"
            >
              Take the {assessment.title} screening
            </Link>
          </div>
        </FramedPanel>
      </HubPageLayout>
    );
  }

  const { score, distribution } = result;
  const rating = getRating(score);

  const cells = panel === 'vulnerabilities'
    ? RATING_BANDS.reduce((acc, b) => ({ ...acc, [b.id]: distribution?.[b.id] ?? 0 }), {})
    : RATING_BANDS.reduce((acc, b) => ({ ...acc, [b.id]: b.rangeLabel }), {});

  const tabClass = (id) =>
    `px-6 py-2.5 rounded-lg text-lg border transition-colors ${
      panel === id
        ? 'bg-gray-600 text-white border-gray-600 font-medium'
        : 'bg-white text-black border-gray-300 hover:bg-gray-50'
    }`;

  return (
    <HubPageLayout>
      {/* Score hero: screening image beside the score panel */}
      <FramedPanel title={assessment.title}>
        <div className="px-6 pt-8 pb-6">
          <div className="flex flex-col md:flex-row">
            <img
              src="/images/mosaic-psyops/results-hero.jpeg"
              alt=""
              className="md:w-1/2 h-[220px] md:h-[300px] object-cover"
            />
            <div className="md:w-1/2 bg-black text-white flex flex-col items-center justify-center py-10 px-6">
              <p className="text-3xl xl:text-4xl font-bold">Your Score / {score}%</p>
              <div className="w-2/3 border-t border-white my-6" />
              <p className="text-3xl xl:text-4xl font-bold">Rating / {rating.label}</p>
            </div>
          </div>
        </div>
      </FramedPanel>

      {/* Vulnerabilities scale vs. response distribution */}
      <div className="flex gap-4 mt-10 mb-4">
        <button type="button" onClick={() => setPanel('vulnerabilities')} className={tabClass('vulnerabilities')}>
          Vulnerabilities Scale
        </button>
        <button type="button" onClick={() => setPanel('distribution')} className={tabClass('distribution')}>
          Response Distribution
        </button>
      </div>

      <BandTable cells={cells} />
      <p className="text-sm text-gray-500 mt-2">
        {panel === 'vulnerabilities'
          ? 'How many of your answers landed in each vulnerability band.'
          : 'The score range each rating band covers.'}
      </p>

      {SECTIONS.map((section) => (
        <SplitSection key={section.heading} {...section} />
      ))}

      <SplitSection heading="Trustworthy" paragraphs={[TRUSTWORTHY]} />

      <img
        src="/images/mosaic-psyops/results-trustworthy.jpeg"
        alt=""
        className="w-full max-h-[340px] object-cover mt-8"
      />

      <Link
        to={assessmentPath(slug)}
        className="inline-block mt-10 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-900"
      >
        Retake the assessment
      </Link>
    </HubPageLayout>
  );
};

export default AssessmentResultsPage;
